import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { toAssetUrl } from '../../utils/assetBase';
import PhoneInput from '../../components/PhoneInput';

// Public ev vitrini — BÜTÜN evlər həmişə görünür (tarix onları gizlətmir).
// Hər evin altında "Rezerv et" → panel açılır: tarix + məlumatlar.
// Seçilən tarixdə ev doludursa → boş evlər qiymət fərqi ilə təklif olunur.

const BRAND = '#0090bb';
const todayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Baku' });
const addDays = (iso, n) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};
const money = (v) => `${Number(v || 0).toFixed(0)} ₼`;
const BASE_CAP = 2;
// Gecəlik qiymət = baza + (nəfər − 2) × əlavə_nəfər
const nightlyOf = (h, guests) =>
    Number(h?.price || 0) + Math.max(0, Number(guests || BASE_CAP) - BASE_CAP) * Number(h?.extra_person_price || 0);
// Beh = cəm × faiz%, yuxarı 10-a yuvarlanır (serverlə eyni; yalnız önizləmə üçün)
const depositOf = (total, percent) => Math.ceil((Number(total || 0) * (Number(percent || 0) / 100)) / 10) * 10;
// Ödənişli nəfər = böyük + (yaş həddindən böyük uşaqlar). Yaş həddinə qədər uşaqlar pulsuz.
const paidPersonsOf = (adults, childAges, freeAge) =>
    Number(adults || 0) + (childAges || []).filter(a => Number(a) > Number(freeAge || 0)).length;

// Alternativ evin cari evə görə üstünlüyünü qısa mətnə çevir
const pitchFor = (alt, cur) => {
    const bits = [];
    if (cur) {
        const diff = Number(alt.price || 0) - Number(cur.price || 0);
        if (diff > 0) bits.push(`+${money(diff)} fərqlə`);
        else if (diff < 0) bits.push(`${money(Math.abs(diff))} ucuz`);
        else bits.push('eyni qiymətə');
        if (Number(alt.area_m2) > Number(cur.area_m2 || 0)) bits.push(`daha geniş (${alt.area_m2} m²)`);
        if (Number(alt.capacity_max) > Number(cur.capacity_max || 0)) bits.push(`${alt.capacity_max} nəfərədək`);
    }
    return bits.join(' · ');
};

export default function Houses() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('t') || '';

    // Tarix — həm boşluğu göstərir, həm rezervasiya tarixidir (tək mənbə)
    const [from, setFrom] = useState(todayISO());
    const [to, setTo] = useState(addDays(todayISO(), 1));
    const [houses, setHouses] = useState([]);
    const [tenant, setTenant] = useState(null);
    const [nights, setNights] = useState(1);
    const [freeHouses, setFreeHouses] = useState(0);   // cəmi boş ev sayı (qruplar üzrə)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [detailId, setDetailId] = useState(null); // qalereya/detal
    const [slide, setSlide] = useState(0);
    const [bookId, setBookId] = useState(null);     // rezerv paneli açıq olan ev

    const [book, setBook] = useState({ guest_name: '', phone: '', adults: BASE_CAP, childAges: [], note: '' });
    const [booking, setBooking] = useState(false);
    const [bookErr, setBookErr] = useState('');
    const [bookDone, setBookDone] = useState('');
    const [phoneValid, setPhoneValid] = useState(false);

    const load = useCallback(async () => {
        if (!token) { setError('Link yanlışdır.'); return; }
        if (to <= from) return;
        setLoading(true); setError('');
        try {
            const res = await api.get('/public-booking/houses', { params: { token, from, to } });
            setHouses(res.data.data || []);
            setTenant(res.data.tenant || null);
            setNights(res.data.nights || 1);
            setFreeHouses(res.data.free_houses || 0);
        } catch (err) {
            setError(err.response?.data?.msg || 'Yüklənmədi.');
            setHouses([]);
        } finally {
            setLoading(false);
        }
    }, [token, from, to]);

    useEffect(() => { load(); }, [load]);

    const onFromChange = (v) => { setFrom(v); if (to <= v) setTo(addDays(v, 1)); };

    // Tarix dəyişəndə `houses` yenilənir → bunlar avtomatik sinxron qalır
    const detail = houses.find(h => h.id === detailId) || null;
    const bookHouse = houses.find(h => h.id === bookId) || null;
    const alternatives = houses.filter(h => h.available && h.id !== bookId);
    const availableCount = houses.filter(h => h.available).length;

    const openDetail = (h) => { setDetailId(h.id); setSlide(0); };
    const openBooking = (h) => {
        setBookId(h.id);
        setDetailId(null);
        setBook({ guest_name: '', phone: '', adults: BASE_CAP, childAges: [], note: '' });
        setBookErr(''); setBookDone(''); setPhoneValid(false);
    };

    const submitBooking = async () => {
        setBookErr('');
        if (!bookHouse) return;
        if (!bookHouse.available) return setBookErr('Bu ev seçilən tarixdə boş deyil.');
        if (!book.guest_name.trim()) return setBookErr('Adınızı yazın.');
        if (!phoneValid) return setBookErr('Telefon nömrəsini tam və düzgün yazın.');
        setBooking(true);
        const payload = {
            token, listing_id: bookHouse.id, check_in: from, check_out: to,
            guest_name: book.guest_name, phone: book.phone,
            adults: book.adults, child_ages: book.childAges, note: book.note
        };
        try {
            if (tenant?.payriff_enabled) {
                // Ödənişli axın: Payriff sifarişi yaradılır → müştəri ödəniş səhifəsinə yönlənir.
                const res = await api.post('/public-booking/pay', payload);
                if (res.data?.payment_url) {
                    window.location.href = res.data.payment_url;
                    return; // yönləndirmə gedir — booking spinner qalsın
                }
                setBookErr('Ödəniş başladıla bilmədi. Yenidən cəhd edin.');
            } else {
                const res = await api.post('/public-booking/request', payload);
                setBookDone(res.data?.msg || 'Sorğunuz göndərildi.');
            }
        } catch (err) {
            setBookErr(err.response?.data?.msg || 'Göndərilmədi. Yenidən cəhd edin.');
            if (err.response?.status === 409) load(); // bu arada dolubsa
        } finally {
            setBooking(false);
        }
    };

    // min-w-0 + box-border + appearance-none → native date input öz min-eninə görə daşmasın
    const dateInput = 'block w-full min-w-0 box-border appearance-none rounded-xl border border-gray-200 px-2.5 py-2 text-sm focus:outline-none';

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur">
                <div className="mx-auto max-w-5xl px-4 py-3">
                    <div className="flex items-center gap-3">
                        {tenant?.logo_url && (
                            <img src={toAssetUrl(tenant.logo_url)} alt="" className="h-9 w-9 rounded-lg object-cover" />
                        )}
                        <div className="min-w-0">
                            <h1 className="truncate text-base font-black text-gray-900">{tenant?.name || 'Evlər'}</h1>
                            <p className="text-[11px] text-gray-400">Evlərimiz — rezervasiya üçün tarix seçin</p>
                        </div>
                        {token && (
                            <a href={`/qrmenu?t=${encodeURIComponent(token)}`}
                                className="ml-auto shrink-0 whitespace-nowrap rounded-xl border px-3 py-2 text-xs font-bold transition-all active:scale-95"
                                style={{ borderColor: BRAND, color: BRAND }}>
                                ← Menyu
                            </a>
                        )}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="min-w-0">
                            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Giriş</label>
                            <input type="date" value={from} min={todayISO()} onChange={(e) => onFromChange(e.target.value)} className={dateInput} />
                        </div>
                        <div className="min-w-0">
                            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Çıxış</label>
                            <input type="date" value={to} min={addDays(from, 1)} onChange={(e) => setTo(e.target.value)} className={dateInput} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-5xl px-4 py-4">
                {error ? (
                    <div className="rounded-2xl border border-red-100 bg-red-50 py-12 text-center text-sm font-semibold text-red-600">{error}</div>
                ) : loading && houses.length === 0 ? (
                    <div className="py-20 text-center text-sm text-gray-400">Yüklənir...</div>
                ) : houses.length === 0 ? (
                    <div className="py-20 text-center text-sm text-gray-400">Ev tapılmadı.</div>
                ) : (
                    <>
                        <p className="mb-3 text-sm font-bold text-gray-700">
                            {nights} gecə · <span style={{ color: BRAND }}>{freeHouses} boş ev</span>
                            {availableCount === 0 && <span className="text-gray-400"> — bu tarixdə boş yer yoxdur</span>}
                            {loading && <span className="ml-2 text-[11px] font-normal text-gray-400">yenilənir...</span>}
                        </p>

                        {/* BÜTÜN evlər — dolu olanlar da göstərilir */}
                        {/* Mobildə 2 sütun — 3:4 portret tək sütunda tam ekran tuturdu */}
                        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-3">
                            {houses.map((h) => (
                                <div key={h.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all hover:shadow-lg">
                                    <button type="button" onClick={() => openDetail(h)} className="block w-full text-left">
                                        <div className="relative aspect-[3/4] bg-gray-100">
                                            {h.images?.[0] ? (
                                                <img src={toAssetUrl(h.images[0])} alt={h.name} className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center text-4xl text-gray-300">🏡</div>
                                            )}
                                            <span className={`absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[9px] font-bold text-white sm:left-2 sm:top-2 sm:px-2.5 sm:py-1 sm:text-[10px] ${h.available ? 'bg-emerald-600' : 'bg-gray-900/80'}`}>
                                                {h.available
                                                    ? `${h.free_count} boş${h.house_count > 1 ? ` / ${h.house_count}` : ''}`
                                                    : `Dolu${h.busy_until ? ` · ${h.busy_until}-dək` : ''}`}
                                            </span>
                                            {h.video_url && <span className="absolute right-1.5 top-1.5 rounded-full bg-black/60 px-1.5 py-0.5 text-[9px] font-bold text-white sm:right-2 sm:top-2 sm:px-2 sm:py-1 sm:text-[10px]">🎬</span>}
                                        </div>
                                        <div className="p-2 sm:p-3">
                                            {/* Dar sütunda ad və qiymət alt-alta, geniş ekranda yan-yana */}
                                            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-2">
                                                <h3 className="truncate text-xs font-bold text-gray-900 sm:text-sm">{h.name}</h3>
                                                {h.price > 0 && (
                                                    <div className="text-right leading-tight">
                                                        <span className="whitespace-nowrap text-xs font-black sm:text-sm" style={{ color: BRAND }}>
                                                            {money(h.price)} manat<span className="text-[9px] font-normal text-gray-400 sm:text-[10px]">/gecə</span>
                                                        </span>
                                                        <div className="text-[9px] font-bold text-emerald-600 sm:text-[10px]">
                                                            {h.base_capacity || 2} nəfər üçün
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {h.extra_person_price > 0 && (
                                                <div className="mt-1 text-[9px] font-semibold text-gray-500 sm:text-[10px]">
                                                    ➕ əlavə hər nəfər +{money(h.extra_person_price)}
                                                </div>
                                            )}
                                            <div className="mt-1 flex flex-wrap gap-x-1.5 gap-y-0.5 text-[10px] leading-tight text-gray-500 sm:mt-1.5 sm:gap-x-2 sm:gap-y-1 sm:text-[11px]">
                                                <span>👤 {h.capacity_ideal}–{h.capacity_max}</span>
                                                {h.area_m2 > 0 && <span>📐 {h.area_m2} m²</span>}
                                                {h.rooms > 0 && <span>🚪 {h.rooms} otaq</span>}
                                            </div>
                                            {h.breakfast_included && (
                                                <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 sm:mt-2 sm:px-2 sm:text-[10px]">
                                                    ✅ Səhər yeməyi
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                    {/* Rezerv et — dolu olsa da açılır (panel alternativ təklif edir) */}
                                    <div className="px-2 pb-2 sm:px-3 sm:pb-3">
                                        <button type="button" onClick={() => openBooking(h)}
                                            className="w-full rounded-xl py-2 text-[10px] font-black uppercase tracking-wide text-white transition-all active:scale-[0.98] sm:py-2.5 sm:text-xs sm:tracking-wider"
                                            style={{ background: h.available ? `linear-gradient(135deg, ${BRAND}, #006f91)` : 'linear-gradient(135deg,#9ca3af,#6b7280)' }}>
                                            {h.available ? '📩 Rezerv et' : 'Alternativ seç'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* ── DETAL (qalereya/video) ── */}
            {detail && (
                <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
                     onClick={(e) => e.target === e.currentTarget && setDetailId(null)}>
                    {/* Bağla — overlay səviyyəsində, HƏMİŞƏ görünür (sürüşmür, notch/status-bar altında qalmır) */}
                    <button type="button" onClick={() => setDetailId(null)}
                        aria-label="Bağla"
                        className="absolute right-3 z-[60] flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-lg text-white shadow-lg backdrop-blur-sm active:scale-95"
                        style={{ top: 'max(0.75rem, env(safe-area-inset-top))' }}>✕</button>
                    <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white sm:rounded-2xl">
                        <div className="relative aspect-[3/4] bg-gray-900">
                            {detail.images?.length > 0 ? (
                                <>
                                    <img src={toAssetUrl(detail.images[slide])} alt="" className="h-full w-full object-cover" />
                                    {detail.images.length > 1 && (
                                        <>
                                            <button type="button" onClick={() => setSlide((s) => (s - 1 + detail.images.length) % detail.images.length)}
                                                className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white">‹</button>
                                            <button type="button" onClick={() => setSlide((s) => (s + 1) % detail.images.length)}
                                                className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white">›</button>
                                            <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white">
                                                {slide + 1}/{detail.images.length}
                                            </span>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="flex h-full items-center justify-center text-5xl text-gray-600">🏡</div>
                            )}
                        </div>

                        <div className="p-4">
                            <div className="flex items-start justify-between gap-2">
                                <h2 className="text-lg font-black text-gray-900">{detail.name}</h2>
                                {detail.price > 0 && (
                                    <div className="text-right">
                                        <div className="text-lg font-black" style={{ color: BRAND }}>{money(detail.price)}</div>
                                        <div className="text-[10px] text-gray-400">gecə başına</div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                <div className="rounded-xl bg-gray-50 px-3 py-2">👤 İdeal {detail.capacity_ideal} · Maks {detail.capacity_max}</div>
                                {detail.area_m2 > 0 && <div className="rounded-xl bg-gray-50 px-3 py-2">📐 {detail.area_m2} m²</div>}
                                {detail.rooms > 0 && <div className="rounded-xl bg-gray-50 px-3 py-2">🚪 {detail.rooms} otaq</div>}
                                {detail.beds > 0 && <div className="rounded-xl bg-gray-50 px-3 py-2">🛏️ {detail.beds} ikinəfərlik çarpayı</div>}
                                {detail.single_beds > 0 && <div className="rounded-xl bg-gray-50 px-3 py-2">🛏️ {detail.single_beds} tək nəfərlik çarpayı</div>}
                                {detail.sofas > 0 && <div className="rounded-xl bg-gray-50 px-3 py-2">🛋️ {detail.sofas} açılan divan</div>}
                                {detail.breakfast_included && <div className="rounded-xl bg-emerald-50 px-3 py-2 font-bold text-emerald-700">✅ Səhər yeməyi daxil</div>}
                            </div>

                            {detail.description && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600">{detail.description}</p>}

                            {detail.amenities?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {detail.amenities.map((a, i) => (
                                        <span key={i} className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">✓ {a}</span>
                                    ))}
                                </div>
                            )}

                            {detail.video_url && (
                                <video src={toAssetUrl(detail.video_url)} controls playsInline
                                    className="mt-3 w-full rounded-xl border border-gray-200 bg-black" />
                            )}

                            <button type="button" onClick={() => openBooking(detail)}
                                className="mt-4 w-full rounded-xl py-3 text-sm font-black uppercase tracking-wider text-white"
                                style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                📩 Rezerv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── REZERVASİYA PANELİ ── */}
            {bookHouse && (
                <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
                     onClick={(e) => e.target === e.currentTarget && setBookId(null)}>
                    <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white sm:rounded-2xl">
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-5 pb-3"
                             style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
                            <div className="min-w-0">
                                <h3 className="truncate text-base font-black text-gray-900">📩 {bookHouse.name}</h3>
                                <p className="text-[11px] text-gray-400">Rezervasiya sorğusu</p>
                            </div>
                            <button type="button" onClick={() => setBookId(null)} aria-label="Bağla"
                                className="-mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl text-gray-400 active:bg-gray-100">✕</button>
                        </div>

                        <div className="p-5">
                            {bookDone ? (
                                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center">
                                    <div className="text-3xl">✅</div>
                                    <p className="mt-1 text-sm font-bold text-emerald-800">{bookDone}</p>
                                    <button type="button" onClick={() => setBookId(null)}
                                        className="mt-3 rounded-xl bg-emerald-600 px-5 py-2 text-sm font-bold text-white">Bağla</button>
                                </div>
                            ) : (
                                <>
                                    {/* Tarix — panelin içində */}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="min-w-0">
                                            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Giriş</label>
                                            <input type="date" value={from} min={todayISO()} onChange={(e) => onFromChange(e.target.value)} className={dateInput} />
                                        </div>
                                        <div className="min-w-0">
                                            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-gray-400">Çıxış</label>
                                            <input type="date" value={to} min={addDays(from, 1)} onChange={(e) => setTo(e.target.value)} className={dateInput} />
                                        </div>
                                    </div>

                                    {/* Status */}
                                    {loading ? (
                                        <p className="mt-3 text-center text-xs text-gray-400">Yoxlanılır...</p>
                                    ) : bookHouse.available ? (
                                        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800">
                                            ✅ Bu tarixdə {bookHouse.free_count} boş
                                            {bookHouse.house_count > 1 ? ` / ${bookHouse.house_count}` : ''} · {nights} gecə
                                            {bookHouse.price > 0 && ` · ≈ ${money(bookHouse.price * nights)}`}
                                        </div>
                                    ) : (
                                        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5">
                                            <p className="text-xs font-bold text-rose-800">
                                                ⚠️ Bu tarixdə <b>{bookHouse.name}</b> boş deyil{bookHouse.busy_until ? ` (${bookHouse.busy_until} tarixinədək dolu)` : ''}.
                                            </p>
                                            <p className="mt-1 text-[11px] text-rose-700/80">Tarixi dəyişin və ya aşağıdakı boş evlərdən birini seçin.</p>
                                        </div>
                                    )}

                                    {/* Alternativ evlər — dolu olanda mütləq, boş olanda "daha yaxşı təklif" */}
                                    {alternatives.length > 0 && (() => {
                                        const list = bookHouse.available
                                            // boşdursa: yalnız DAHA GENİŞ/böyük olanları təklif et
                                            ? alternatives.filter(a => Number(a.area_m2) > Number(bookHouse.area_m2 || 0) || Number(a.capacity_max) > Number(bookHouse.capacity_max || 0))
                                            : alternatives;
                                        if (!list.length) return null;
                                        return (
                                            <div className="mt-3">
                                                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                                                    {bookHouse.available ? '✨ Daha yaxşı təklif' : 'Bu tarixdə boş olan evlər'}
                                                </p>
                                                <div className="space-y-1.5">
                                                    {list.map((a) => (
                                                        <button key={a.id} type="button" onClick={() => { setBookId(a.id); setBookErr(''); }}
                                                            className="flex w-full items-center gap-2 rounded-xl border border-gray-200 p-2 text-left transition-all hover:border-primary-400 hover:bg-primary-50/30">
                                                            {a.images?.[0] ? (
                                                                <img src={toAssetUrl(a.images[0])} alt="" className="h-11 w-14 shrink-0 rounded-lg object-cover" />
                                                            ) : (
                                                                <div className="flex h-11 w-14 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-300">🏡</div>
                                                            )}
                                                            <div className="min-w-0 flex-1">
                                                                <div className="text-xs font-bold text-gray-800">{a.name}</div>
                                                                <div className="text-[10px] text-gray-500">{pitchFor(a, bookHouse) || `${money(a.price)}/gecə`}</div>
                                                            </div>
                                                            <span className="shrink-0 text-xs font-black" style={{ color: BRAND }}>{money(a.price)}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Məlumatlar */}
                                    <div className="mt-4 space-y-2">
                                        <input type="text" value={book.guest_name}
                                            onChange={(e) => setBook({ ...book, guest_name: e.target.value })}
                                            placeholder="Ad, Soyad"
                                            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none" />
                                        <PhoneInput onChange={(full, valid) => { setBook(b => ({ ...b, phone: full })); setPhoneValid(valid); }} />
                                        {/* Böyük + Uşaq (hər uşağın yaşı) — yaş həddindən böyük uşaqlar qiymətə daxil */}
                                        {(() => {
                                            const maxCap = Number(bookHouse.capacity_max || BASE_CAP);
                                            const freeAge = Number(tenant?.child_free_age || 0);
                                            const adults = Math.max(BASE_CAP, Number(book.adults) || BASE_CAP);
                                            const childAges = Array.isArray(book.childAges) ? book.childAges : [];
                                            const occupancy = adults + childAges.length;
                                            const canAddMore = occupancy < maxCap;
                                            const setAdults = (v) => {
                                                const nv = Math.max(BASE_CAP, v);
                                                if (nv + childAges.length > maxCap) return;
                                                setBook({ ...book, adults: nv });
                                            };
                                            // Default seçili yaş ÖDƏNİŞLİ olsun (yaş həddindən yuxarı) — müştəri diqqət
                                            // etməsə də pulsuz keçməsin; adətən 10, yaş həddi böyükdürsə həddən +1.
                                            const childDefaultAge = Math.min(17, Math.max(10, freeAge + 1));
                                            const addChild = () => { if (canAddMore) setBook({ ...book, childAges: [...childAges, childDefaultAge] }); };
                                            const removeChild = (i) => setBook({ ...book, childAges: childAges.filter((_, j) => j !== i) });
                                            const setChildAge = (i, age) => setBook({ ...book, childAges: childAges.map((a, j) => j === i ? age : a) });

                                            const paidPersons = paidPersonsOf(adults, childAges, freeAge);
                                            const nightly = nightlyOf(bookHouse, paidPersons);
                                            const extraCount = Math.max(0, paidPersons - BASE_CAP);
                                            return (
                                                <div className="space-y-2">
                                                    {/* Böyük sayı */}
                                                    <div className="rounded-xl border border-gray-200 p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <div className="text-sm font-bold text-gray-800">Böyük</div>
                                                                <div className="text-[11px] text-gray-400">min {BASE_CAP} · maks tutum {maxCap}</div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <button type="button" onClick={() => setAdults(adults - 1)} disabled={adults <= BASE_CAP}
                                                                    className="flex h-9 w-9 items-center justify-center rounded-full border-2 text-lg font-black transition-all disabled:opacity-30"
                                                                    style={{ borderColor: BRAND, color: BRAND }}>−</button>
                                                                <span className="w-6 text-center text-lg font-black text-gray-900">{adults}</span>
                                                                <button type="button" onClick={() => setAdults(adults + 1)} disabled={occupancy >= maxCap}
                                                                    className="flex h-9 w-9 items-center justify-center rounded-full border-2 text-lg font-black transition-all disabled:opacity-30"
                                                                    style={{ borderColor: BRAND, color: BRAND }}>+</button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Uşaqlar — hər biri üçün yaş */}
                                                    <div className="rounded-xl border border-gray-200 p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <div className="text-sm font-bold text-gray-800">Uşaq</div>
                                                                <div className="text-[11px] text-gray-400">{freeAge} yaşa qədər pulsuz</div>
                                                            </div>
                                                            <button type="button" onClick={addChild} disabled={!canAddMore}
                                                                className="rounded-full border-2 px-3 py-1.5 text-xs font-black transition-all disabled:opacity-30"
                                                                style={{ borderColor: BRAND, color: BRAND }}>+ Uşaq əlavə et</button>
                                                        </div>
                                                        {childAges.length > 0 && (
                                                            <div className="mt-2 space-y-1.5 border-t border-gray-100 pt-2">
                                                                {childAges.map((age, i) => {
                                                                    const paid = Number(age) > freeAge;
                                                                    return (
                                                                        <div key={i} className="flex items-center gap-2">
                                                                            <span className="text-xs font-semibold text-gray-500">{i + 1}. uşaq</span>
                                                                            <select value={age} onChange={(e) => setChildAge(i, Number(e.target.value))}
                                                                                className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:outline-none">
                                                                                {Array.from({ length: 18 }, (_, y) => (
                                                                                    <option key={y} value={y}>{y} yaş</option>
                                                                                ))}
                                                                            </select>
                                                                            <span className={`text-[10px] font-bold ${paid ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                                                {paid ? 'ödənişli' : 'pulsuz'}
                                                                            </span>
                                                                            <button type="button" onClick={() => removeChild(i)}
                                                                                className="ml-auto flex h-6 w-6 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100">✕</button>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Qiymət təfərrüatı */}
                                                    {bookHouse.price > 0 && (
                                                        <div className="rounded-xl border border-gray-200 p-3 text-xs text-gray-500">
                                                            {extraCount > 0 ? (
                                                                <div className="flex justify-between">
                                                                    <span>{money(bookHouse.price)} + {extraCount} × {money(bookHouse.extra_person_price)}</span>
                                                                    <span className="font-bold text-gray-700">{money(nightly)}/gecə</span>
                                                                </div>
                                                            ) : (
                                                                <div className="flex justify-between">
                                                                    <span>Gecəlik ({paidPersons} ödənişli nəfər)</span>
                                                                    <span className="font-bold text-gray-700">{money(nightly)}</span>
                                                                </div>
                                                            )}
                                                            <div className="mt-1 flex justify-between text-sm font-black" style={{ color: BRAND }}>
                                                                <span>{nights} gecə üçün</span>
                                                                <span>{money(nightly * nights)}</span>
                                                            </div>
                                                            {tenant?.payriff_enabled && (
                                                                <div className="mt-2 flex items-center justify-between rounded-lg bg-emerald-50 px-2.5 py-2 text-sm font-black text-emerald-700">
                                                                    <span>💳 İndi ödəniləcək beh ({tenant.deposit_percent || 30}%)</span>
                                                                    <span>{money(depositOf(nightly * nights, tenant.deposit_percent || 30))}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <textarea rows={2} value={book.note}
                                            onChange={(e) => setBook({ ...book, note: e.target.value })}
                                            placeholder="Əlavə qeyd (istəyə bağlı)"
                                            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none" />
                                    </div>

                                    {bookErr && <p className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{bookErr}</p>}

                                    <button type="button" onClick={submitBooking} disabled={booking || !bookHouse.available}
                                        className="mt-3 w-full rounded-xl py-3 text-sm font-black text-white transition-all disabled:cursor-not-allowed disabled:opacity-40"
                                        style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                        {booking
                                            ? (tenant?.payriff_enabled ? 'Ödənişə yönləndirilir...' : 'Göndərilir...')
                                            : !bookHouse.available ? 'Bu tarixdə boş deyil'
                                            : tenant?.payriff_enabled ? '💳 Beh ödə və rezerv et'
                                            : '📩 Rezervasiya sorğusu göndər'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
